import { Chat, useOnStreamEnd } from "@launch10/use-langgraph-stream";
import { CHAT_PERSONAS } from "@components/chat/chatPersonas";
import { router } from "@inertiajs/react";
import { useAdsStreamOptions, useAdsChatIsLoadingHistory } from "@components/ads/hooks";
import { useAdsComposer } from "@components/ads/hooks/useAdsComposer";
import { ChatProvider } from "@components/shared/chat/ChatProvider";
import { useCreditGateProps } from "@hooks/shared";
import AdCampaignTabSwitcher from "@components/ads/AdCampaignTabSwitcher";
import LogoSpinner from "@components/ui/logo-spinner";
import AdPreview from "@components/ads/AdPreview";
import AdsForm from "@components/ads/forms/AdsForm";
import { PaginationFooterDefault as AdsPaginationFooter } from "@components/shared/pagination-footer";
import WorkflowPanel from "@components/ads/WorkflowPanel";
import { Workspace } from "@components/shared/workspace/Workspace";
import { selectSubstep, useWorkflow } from "@context/WorkflowProvider";
import { cn } from "@lib/utils";
import { FirstCampaignNotification } from "@components/app-notifications";

export default function Ads() {
  const streamOptions = useAdsStreamOptions();
  const useComposer = useAdsComposer();
  const creditGate = useCreditGateProps();

  return (
    <Chat.Root streamOptions={streamOptions} useComposer={useComposer} personas={CHAT_PERSONAS} {...creditGate}>
      <ChatProvider name="ads" syncKeys={["websiteId", "projectId", "campaignId"]} />
      <AdsContent />
    </Chat.Root>
  );
}

function AdsContent() {
  const isLoadingHistory = useAdsChatIsLoadingHistory();
  const substep = useWorkflow(selectSubstep);

  useOnStreamEnd(() => {
    router.reload({
      only: [
        "campaign",
        "ad_group",
        "ad",
        "headlines",
        "descriptions",
        "callouts",
        "keywords",
        "negative_keywords",
        "structured_snippet",
        "ad_schedule",
        "location_targets",
        "languages",
      ],
    });
  });

  const shouldHideTabSwitcher = substep === "launch" || substep === "review";

  return (
    <Workspace
      banner={<FirstCampaignNotification />}
      chat={<WorkflowPanel />}
      artifacts={
        // The ads artifacts: preview on top, then the stage's edit form.
        // The forms scroll inside the panel (the website preview manages
        // its own scroll); bottom padding clears the footer bleed.
        <div className="flex-1 min-h-0 overflow-y-auto pb-24 flex flex-col gap-2">
          <AdPreview className="shrink-0" />
          <div>
            {!shouldHideTabSwitcher && (
              <AdCampaignTabSwitcher disabled={isLoadingHistory} />
            )}
            {isLoadingHistory ? (
              <div
                className={cn(
                  "border-[#D3D2D0] border rounded-2xl bg-white",
                  !shouldHideTabSwitcher && "rounded-t-none"
                )}
              >
                <div className="flex items-center justify-center p-9">
                  <LogoSpinner />
                </div>
              </div>
            ) : (
              <AdsForm />
            )}
          </div>
        </div>
      }
      footer={!isLoadingHistory ? <AdsPaginationFooter /> : undefined}
    />
  );
}
